import * as XLSX from 'xlsx-js-style';

export type ApprovalExportRow = {
  statusKey: string;
  status: string;
  table: string;
  date: string;
  time: string;
  type: string;
  requestedBy: string;
  decidedBy: string;
  reason: string;
  amount: string;
  items: string;
  note: string;
};

const toSafeFileBaseName = (value: string): string => value
  .trim()
  .replace(/[\\/:*?"<>|]/g, '-')
  .replace(/\s+/g, ' ')
  .replace(/-+/g, '-')
  .replace(/^[.\-\s]+|[.\-\s]+$/g, '') || 'בקשות אישור';

const toSafeSheetName = (value: string): string => {
  const normalized = value
    .trim()
    .replace(/[\\/:*?\[\]]/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/-+/g, '-')
    .replace(/^[.\-\s]+|[.\-\s]+$/g, '');

  if (!normalized) {
    return 'בקשות אישור';
  }

  // Excel sheet names are limited to 31 characters.
  return normalized.slice(0, 31);
};

export const downloadApprovalRequestsExcel = (rows: ApprovalExportRow[], title: string): void => {
  const data = [
    [title],
    ['סטטוס', 'שולחן', 'תאריך', 'שעה', 'סוג פעולה', 'בוקש ע"י', 'אושר/נדחה ע"י', 'סיבה', 'סכום הנחה', 'פריטי הזמנה', 'הערה'],
    ...rows.map((row) => [
      row.status,
      row.table,
      row.date,
      row.time,
      row.type,
      row.requestedBy,
      row.decidedBy,
      row.reason,
      row.amount,
      row.items,
      row.note,
    ]),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(data);
  worksheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 10 } },
  ];
  worksheet['!cols'] = [
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 10 },
    { wch: 20 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 55 },
    { wch: 24 },
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, toSafeSheetName(title));
  workbook.Workbook = workbook.Workbook ?? {};
  workbook.Workbook.Views = [{ RTL: true }];

  const lastColumn = 10;
  const toColumnName = (index: number): string => String.fromCharCode(65 + index);
  const setRowBold = (rowNumber: number) => {
    for (let column = 0; column <= lastColumn; column += 1) {
      const cellAddress = `${toColumnName(column)}${rowNumber}`;
      const cell = worksheet[cellAddress];
      if (!cell) {
        continue;
      }
      const currentStyle = (cell as XLSX.CellObject & { s?: Record<string, unknown> }).s ?? {};
      const currentFont = (currentStyle.font as Record<string, unknown> | undefined) ?? {};
      (cell as XLSX.CellObject & { s?: Record<string, unknown> }).s = {
        ...currentStyle,
        font: {
          ...currentFont,
          bold: true,
        },
      };
    }
  };

  const setRowCenter = (rowNumber: number) => {
    for (let column = 0; column <= lastColumn; column += 1) {
      const cellAddress = `${toColumnName(column)}${rowNumber}`;
      const cell = worksheet[cellAddress];
      if (!cell) {
        continue;
      }
      const currentStyle = (cell as XLSX.CellObject & { s?: Record<string, unknown> }).s ?? {};
      const currentAlignment = (currentStyle.alignment as Record<string, unknown> | undefined) ?? {};
      (cell as XLSX.CellObject & { s?: Record<string, unknown> }).s = {
        ...currentStyle,
        alignment: {
          ...currentAlignment,
          horizontal: 'center',
        },
      };
    }
  };

  setRowBold(1);
  setRowBold(2);
  setRowCenter(2);

  // Make first column (status) bold, and color each status value by meaning.
  const statusColorByKey: Record<string, string> = {
    pending: 'FFD97706',
    approved: 'FF15803D',
    denied: 'FFB91C1C',
  };

  for (let rowNumber = 1; rowNumber <= rows.length + 2; rowNumber += 1) {
    const cellAddress = `A${rowNumber}`;
    const cell = worksheet[cellAddress];
    if (!cell) {
      continue;
    }
    const currentStyle = (cell as XLSX.CellObject & { s?: Record<string, unknown> }).s ?? {};
    const currentFont = (currentStyle.font as Record<string, unknown> | undefined) ?? {};
    (cell as XLSX.CellObject & { s?: Record<string, unknown> }).s = {
      ...currentStyle,
      font: {
        ...currentFont,
        bold: true,
      },
    };
  }

  rows.forEach((row, index) => {
    const rowNumber = index + 3;
    const color = statusColorByKey[row.statusKey];
    if (!color) {
      return;
    }
    const cellAddress = `A${rowNumber}`;
    const cell = worksheet[cellAddress];
    if (!cell) {
      return;
    }
    const currentStyle = (cell as XLSX.CellObject & { s?: Record<string, unknown> }).s ?? {};
    const currentFont = (currentStyle.font as Record<string, unknown> | undefined) ?? {};
    (cell as XLSX.CellObject & { s?: Record<string, unknown> }).s = {
      ...currentStyle,
      font: {
        ...currentFont,
        bold: true,
        color: { rgb: color },
      },
    };
  });

  // Center all columns except "פריטי הזמנה" (J), which stays right-aligned.
  const totalRows = rows.length + 2;
  for (let rowNumber = 1; rowNumber <= totalRows; rowNumber += 1) {
    for (let column = 0; column <= lastColumn; column += 1) {
      const cellAddress = `${toColumnName(column)}${rowNumber}`;
      const cell = worksheet[cellAddress];
      if (!cell) {
        continue;
      }
      const currentStyle = (cell as XLSX.CellObject & { s?: Record<string, unknown> }).s ?? {};
      const currentAlignment = (currentStyle.alignment as Record<string, unknown> | undefined) ?? {};
      (cell as XLSX.CellObject & { s?: Record<string, unknown> }).s = {
        ...currentStyle,
        alignment: {
          ...currentAlignment,
          horizontal: column === 9 ? 'right' : 'center',
          vertical: 'top',
          wrapText: true,
        },
      };
    }
  }

  const fileBaseName = toSafeFileBaseName(title);
  XLSX.writeFile(workbook, `${fileBaseName}.xlsx`, { cellStyles: true });
};

const escapeHtml = (value: string): string => value
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#39;');

const buildApprovalRequestsPdfHtml = (rows: ApprovalExportRow[], title: string): string => {
  const rowHtml = rows.length > 0
    ? rows.map((row) => {
      const statusClass = row.statusKey === 'pending'
        ? 'status-pending'
        : row.statusKey === 'approved'
          ? 'status-approved'
          : row.statusKey === 'denied'
            ? 'status-denied'
            : '';
      return `<tr>
        <td class="status ${statusClass}">${escapeHtml(row.status)}</td>
        <td>${escapeHtml(row.table)}</td>
        <td>${escapeHtml(row.date)}</td>
        <td>${escapeHtml(row.time)}</td>
        <td>${escapeHtml(row.type)}</td>
        <td>${escapeHtml(row.requestedBy)}</td>
        <td>${escapeHtml(row.decidedBy)}</td>
        <td>${escapeHtml(row.reason)}</td>
        <td>${escapeHtml(row.amount)}</td>
        <td class="items">${escapeHtml(row.items)}</td>
        <td>${escapeHtml(row.note)}</td>
      </tr>`;
    }).join('')
    : '<tr><td class="empty" colspan="11">No approval requests found</td></tr>';

  return `<!doctype html>
<html lang="en" dir="rtl">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; background: #fff; color: #111827; font-family: Arial, sans-serif; direction: rtl; }
      .host { padding: 16px; }
      h1 { margin: 0; font-size: 22px; font-weight: 700; text-align: right; }
      table { width: 100%; border-collapse: collapse; font-size: 11px; }
      th, td { border: 1px solid #d1d5db; padding: 6px 8px; text-align: right; vertical-align: top; }
      th { background: #f3f4f6; font-weight: 700; }
      td.status { font-weight: 700; white-space: nowrap; }
      td.status.status-pending { color: #d97706; }
      td.status.status-approved { color: #15803d; }
      td.status.status-denied { color: #b91c1c; }
      td.items { white-space: normal; }
      td.empty { text-align: center; color: #6b7280; padding: 16px; }
      @page { size: A4 landscape; margin: 8mm; }
      @media print {
        .host { padding: 0; }
        thead { display: table-header-group; }
        tr { break-inside: avoid; }
      }
    </style>
  </head>
  <body>
    <div class="host">
      <h1>${escapeHtml(title)}</h1>
      <table>
        <thead>
          <tr>
            <th>סטטוס</th>
            <th>שולחן</th>
            <th>תאריך</th>
            <th>שעה</th>
            <th>סוג פעולה</th>
            <th>בוקש ע"י</th>
            <th>אושר/נדחה ע"י</th>
            <th>סיבה</th>
            <th>סכום הנחה</th>
            <th>פריטי הזמנה</th>
            <th>הערה</th>
          </tr>
        </thead>
        <tbody>${rowHtml}</tbody>
      </table>
    </div>
  </body>
</html>`;
};

export const printApprovalRequestsPdf = (rows: ApprovalExportRow[], title: string): void => {
  const html = buildApprovalRequestsPdfHtml(rows, title);
  const previousDocumentTitle = document.title;
  document.title = title;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.visibility = 'hidden';
  document.body.appendChild(iframe);

  const cleanup = () => {
    document.title = previousDocumentTitle;
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  };

  const runPrint = () => {
    const frameWindow = iframe.contentWindow;
    if (!frameWindow) {
      cleanup();
      return;
    }
    try {
      frameWindow.focus();
      frameWindow.print();
    } finally {
      setTimeout(cleanup, 500);
    }
  };

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    cleanup();
    return;
  }

  iframe.addEventListener('load', runPrint, { once: true });
  doc.open();
  doc.write(html);
  doc.close();
};
